import { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Lock } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  createEvent,
  updateEvent,
  uploadStoreImage,
  getSettings,
  listEventCategories,
} from "@/lib/api/admin";
import type { StoreEvent, PlaceDetails, EventCategory } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/Textarea";
import { TextInput } from "@/components/TextInput";
import { Select } from "@/components/Select";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { ImageUpload } from "@/components/ImageUpload";
import { DateTimeInput } from "@/components/DateTimeInput";
import {
  toLocalDatetimeValue,
  localDatetimeToIso,
  defaultStartTime,
  defaultEndTime,
} from "@/lib/utils/date";
import { parseCents } from "@/lib/utils/number";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";

// Radix Select forbids an item value of "" (reserved for "nothing selected") — stand-in for a clickable "None" row.
const NO_CATEGORY = "none";

// A missing/unrecognized visibility_scope (e.g. an unmigrated backend) falls back to "all" rather than hiding the category.
function categoryMatchesVisibility(category: EventCategory, visibility: "public" | "unlisted") {
  const scope =
    category.visibility_scope === "public" || category.visibility_scope === "unlisted"
      ? category.visibility_scope
      : "all";
  return scope === "all" || scope === visibility;
}

// ─── Schema ───────────────────────────────────────────────────────────────────

// Whether any venue field has a value — venue is all-or-nothing across all 6 fields.
const venueTouched = (d: {
  venueName: string;
  venueAddressLine1: string;
  venueAddressLine2: string;
  venueCity: string;
  venueProvince: string;
  venuePostalCode: string;
}) =>
  Boolean(
    d.venueName.trim() ||
    d.venueAddressLine1.trim() ||
    d.venueAddressLine2.trim() ||
    d.venueCity.trim() ||
    d.venueProvince.trim() ||
    d.venuePostalCode.trim()
  );

function makeEventSchema(maxPriceDollars: number) {
  return (
    z
      .object({
        title: z
          .string()
          .min(2, "Title must be at least 2 characters.")
          .max(50, "Title must be 50 characters or fewer."),
        subtitle: z.string().max(50, "Subtitle must be 50 characters or fewer."),
        description: z.string().max(1200, "Description must be 1200 characters or fewer."),
        points: z.string().refine((v) => {
          const n = parseInt(v, 10);
          return !isNaN(n) && n >= 0 && n <= 300;
        }, "Points must be between 0 and 300."),
        price: z
          .string()
          .refine(
            (v) => !isNaN(parseFloat(v)) && parseFloat(v) >= 1,
            "Price must be at least $1.00."
          )
          .refine(
            (v) => parseFloat(v) <= maxPriceDollars,
            `Price cannot exceed $${maxPriceDollars.toLocaleString()}.`
          ),
        discountedPrice: z.string().refine((v) => {
          return !isNaN(parseFloat(v)) && parseFloat(v) >= 1 && parseFloat(v) <= maxPriceDollars;
        }, `Discounted price must be between $1.00 and $${maxPriceDollars.toLocaleString()}.`),
        guestLimitPerEvent: z.string().refine((v) => {
          if (v.trim() === "") return true;
          const n = parseInt(v, 10);
          return !isNaN(n) && n >= 1;
        }, "Guest limit must be at least 1."),
        guestLimitPerSignup: z.string().refine((v) => {
          if (v.trim() === "") return true;
          const n = parseInt(v, 10);
          return !isNaN(n) && n >= 1;
        }, "Must be at least 1."),
        startsAt: z.string().min(1, "Start time is required."),
        endsAt: z.string().min(1, "End time is required."),
        visibility: z.enum(["public", "unlisted"]),
        categoryId: z.string(),
        hasVenue: z.boolean(),
        venueName: z.string().max(100, "Venue name must be 100 characters or fewer."),
        venueAddressLine1: z.string(),
        venueAddressLine2: z.string(),
        venueCity: z.string(),
        venueProvince: z.string(),
        venuePostalCode: z.string(),
      })
      .refine((d) => new Date(d.startsAt) < new Date(d.endsAt), {
        message: "End time must be after start time.",
        path: ["endsAt"],
      })
      .refine(
        (d) => {
          const sub = d.subtitle.trim();
          return !sub || sub.length >= 2;
        },
        {
          message: "Subtitle must be at least 2 characters.",
          path: ["subtitle"],
        }
      )
      .refine(
        (d) => {
          return parseFloat(d.discountedPrice) <= parseFloat(d.price);
        },
        {
          message: "Discounted price must not exceed the retail price.",
          path: ["discountedPrice"],
        }
      )
      // Only enforced while the venue toggle is on.
      .refine((d) => !d.hasVenue || !venueTouched(d) || d.venueName.trim() !== "", {
        message: "Venue name is required when setting a venue.",
        path: ["venueName"],
      })
      .refine((d) => !d.hasVenue || !venueTouched(d) || d.venueAddressLine1.trim() !== "", {
        message: "Address is required when setting a venue.",
        path: ["venueAddressLine1"],
      })
      .refine((d) => !d.hasVenue || !venueTouched(d) || d.venueCity.trim() !== "", {
        message: "City is required when setting a venue.",
        path: ["venueCity"],
      })
      .refine((d) => !d.hasVenue || !venueTouched(d) || d.venueProvince.trim() !== "", {
        message: "Province is required when setting a venue.",
        path: ["venueProvince"],
      })
  );
}

type EventFormValues = z.infer<ReturnType<typeof makeEventSchema>>;

// ─── Event Form Dialog ────────────────────────────────────────────────────────

type EventFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: (event: StoreEvent) => void;
  storeId: string;
  priceLimitBypass: boolean;
  storeCategory: string;
  event?: StoreEvent;
  isDuplicate?: boolean;
  defaultUnlisted?: boolean;
};

export function EventFormDialog({
  open,
  onClose,
  onSaved,
  storeId,
  priceLimitBypass,
  storeCategory,
  event,
  isDuplicate,
  defaultUnlisted,
}: EventFormDialogProps) {
  const user = useAdminAuth();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [posterUrl, setPosterUrl] = useState(event?.poster_url);
  const [posterError, setPosterError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conditions, setConditions] = useState<string[]>(event?.conditions ?? []);
  const [conditionInput, setConditionInput] = useState("");
  const [conditionError, setConditionError] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["event-categories", storeId],
    queryFn: () => listEventCategories(user!, storeId),
    enabled: !!user && open,
  });

  const maxPriceDollars = priceLimitBypass
    ? BYPASS_MAX_PRICE_DOLLARS
    : storeCategory === "nightclub"
      ? (settings?.nightclub_max_price_cents ?? 50000) / 100
      : (settings?.default_max_price_cents ?? 30000) / 100;

  const eventSchema = useMemo(() => makeEventSchema(maxPriceDollars), [maxPriceDollars]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: event?.title ?? "",
      subtitle: event?.subtitle ?? "",
      description: event?.description ?? "",
      points: event ? String(event.points) : "100",
      price: event ? (event.price_cents / 100).toFixed(2) : "",
      discountedPrice: event ? (event.discounted_price_cents / 100).toFixed(2) : "",
      guestLimitPerEvent: event?.guest_limit_per_event ? String(event.guest_limit_per_event) : "",
      guestLimitPerSignup: event?.guest_limit_per_signup
        ? String(event.guest_limit_per_signup)
        : "",
      startsAt:
        event && !isDuplicate
          ? toLocalDatetimeValue(event.starts_at)
          : isDuplicate
            ? ""
            : defaultStartTime(),
      endsAt:
        event && !isDuplicate
          ? toLocalDatetimeValue(event.ends_at)
          : isDuplicate
            ? ""
            : defaultEndTime(defaultStartTime()),
      visibility: event?.visibility ?? (defaultUnlisted ? "unlisted" : "public"),
      categoryId: event?.category_id ?? "",
      hasVenue: !!(event?.venue_name || event?.venue_address_line1),
      venueName: event?.venue_name ?? "",
      venueAddressLine1: event?.venue_address_line1 ?? "",
      venueAddressLine2: event?.venue_address_line2 ?? "",
      venueCity: event?.venue_city ?? "",
      venueProvince: event?.venue_province ?? "",
      venuePostalCode: event?.venue_postal_code ?? "",
    },
  });

  // Backend rejects editing an event whose ends_at has already passed.
  const isPastEvent = !!event && !isDuplicate && new Date(event.ends_at) < new Date();
  // Hint text on a locked field just reads as clutter — every field is already populated.
  const ph = (text: string) => (isPastEvent ? undefined : text);

  const hasVenue = watch("hasVenue");
  const visibility = watch("visibility");
  const pointsField = register("points");
  const guestLimitPerEventField = register("guestLimitPerEvent");
  const guestLimitPerSignupField = register("guestLimitPerSignup");
  const priceField = register("price");
  const discountedPriceField = register("discountedPrice");

  const visibleCategories = useMemo(
    () => categories.filter((c) => categoryMatchesVisibility(c, visibility)),
    [categories, visibility]
  );

  const conditionsDirty =
    JSON.stringify(conditions) !== JSON.stringify(event?.conditions ?? []) ||
    conditionInput.trim() !== "";

  function tryClose() {
    if (isDirty || imageFile !== null || posterUrl !== event?.poster_url || conditionsDirty) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: EventFormValues) {
    // Auto-save any pending condition text using the same rule as the Add button.
    const trimmedCondition = conditionInput.trim();
    let finalConditions = conditions;
    if (trimmedCondition) {
      if (trimmedCondition.length < 3) {
        setConditionError("Must be at least 3 characters.");
        return;
      }
      if (conditions.length < 5) {
        finalConditions = [...conditions, trimmedCondition];
        setConditions(finalConditions);
        setConditionInput("");
        setConditionError(null);
      }
    }

    setIsSubmitting(true);
    try {
      let poster_url: string | undefined = posterUrl;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "event_poster");
        poster_url = res.url;
      } else if (isDuplicate && poster_url) {
        // Re-upload as a new object so deleting either event later doesn't affect the other's poster.
        const posterRes = await fetch(poster_url);
        if (!posterRes.ok) {
          throw new Error(
            "Original poster image is no longer available. Please select a new image."
          );
        }
        const blob = await posterRes.blob();
        const file = new File([blob], "poster", { type: blob.type });
        const res = await uploadStoreImage(user!, storeId, file, "event_poster");
        poster_url = res.url;
      }
      if (!poster_url) {
        setPosterError("Poster image is required");
        setIsSubmitting(false);
        return;
      }
      setPosterError(null);

      const parsedGuestLimitPerEvent = values.guestLimitPerEvent.trim()
        ? parseInt(values.guestLimitPerEvent, 10)
        : null;
      const parsedGuestLimitPerSignup = values.guestLimitPerSignup.trim()
        ? parseInt(values.guestLimitPerSignup, 10)
        : null;
      const payload: Partial<StoreEvent> = {
        title: values.title.trim(),
        subtitle: values.subtitle.trim() || undefined,
        description: values.description.trim() || undefined,
        points: parseInt(values.points, 10),
        price_cents: parseCents(values.price),
        discounted_price_cents: parseCents(values.discountedPrice),
        guest_limit_per_event: parsedGuestLimitPerEvent,
        guest_limit_per_signup: parsedGuestLimitPerSignup,
        starts_at: localDatetimeToIso(values.startsAt),
        ends_at: localDatetimeToIso(values.endsAt),
        conditions: finalConditions,
        visibility: values.visibility,
        category_id: values.categoryId || undefined,
        poster_url,
        venue_name: values.hasVenue ? values.venueName.trim() || undefined : "",
        venue_address_line1: values.hasVenue ? values.venueAddressLine1.trim() || undefined : "",
        venue_address_line2: values.hasVenue ? values.venueAddressLine2.trim() || undefined : "",
        venue_city: values.hasVenue ? values.venueCity.trim() || undefined : "",
        venue_province: values.hasVenue ? values.venueProvince.trim() || undefined : "",
        venue_postal_code: values.hasVenue ? values.venuePostalCode.trim() || undefined : "",
      };

      const savedEvent =
        event && !isDuplicate
          ? await updateEvent(user!, storeId, event.id, payload)
          : await createEvent(user!, storeId, payload);

      toast.success(event && !isDuplicate ? "Event updated." : "Event created.");
      onSaved?.(savedEvent);
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {event && !isDuplicate
                ? isPastEvent
                  ? "View Event"
                  : "Edit Event"
                : isDuplicate
                  ? "Duplicate Event"
                  : "New Event"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 py-2">
            {isPastEvent && (
              <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                <Lock className="size-4 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  This event has already ended and can no longer be edited.
                </p>
              </div>
            )}
            <fieldset disabled={isPastEvent} className="space-y-4">
              {/* Title */}
              <TextInput
                id="ev-title"
                label="Title *"
                placeholder={ph("Ladies Night")}
                maxLength={50}
                error={errors.title?.message}
                {...register("title")}
              />

              {/* Subtitle */}
              <TextInput
                id="ev-sub"
                label="Subtitle"
                placeholder={ph("Every Friday")}
                maxLength={50}
                error={errors.subtitle?.message}
                {...register("subtitle")}
              />
            </fieldset>
            {/* Description sits outside the fieldset — disabled would block scrolling long text. */}
            <Textarea
              id="ev-desc"
              label="Description"
              rows={3}
              maxLength={1200}
              readOnly={isPastEvent}
              className={
                isPastEvent
                  ? "opacity-50 cursor-default focus-visible:ring-0 focus-visible:border-input"
                  : undefined
              }
              error={errors.description?.message}
              {...register("description")}
            />
            <fieldset disabled={isPastEvent} className="space-y-4">
              {/* Poster */}
              <ImageUpload
                label="Poster"
                labelExtra={
                  <span className="text-xs text-muted-foreground font-normal">
                    1920×1080 (16:9)
                  </span>
                }
                aspectRatio="16:9"
                enableCrop
                currentUrl={posterUrl}
                error={posterError ?? undefined}
                onFileSelect={(f) => {
                  setImageFile(f);
                  setPosterError(null);
                }}
                onClear={() => {
                  setImageFile(null);
                  setPosterUrl(undefined);
                }}
                readOnly={isPastEvent}
              />

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name="startsAt"
                  render={({ field }) => (
                    <DateTimeInput
                      id="ev-start"
                      label="Starts at *"
                      error={errors.startsAt?.message}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="endsAt"
                  render={({ field }) => (
                    <DateTimeInput
                      id="ev-end"
                      label="Ends at *"
                      error={errors.endsAt?.message}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>

              {/* Points + Guest Limit */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="ev-pts">Points awarded</Label>
                  <Input
                    id="ev-pts"
                    type="text"
                    inputMode="numeric"
                    placeholder={ph("0–300")}
                    aria-invalid={!!errors.points}
                    {...pointsField}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                      pointsField.onChange(e);
                    }}
                  />
                  {errors.points && (
                    <p className="text-xs text-destructive">{errors.points.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ev-guest">Guest limit (optional)</Label>
                  <Input
                    id="ev-guest"
                    type="text"
                    inputMode="numeric"
                    placeholder={ph("Leave empty for unlimited")}
                    aria-invalid={!!errors.guestLimitPerEvent}
                    {...guestLimitPerEventField}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                      guestLimitPerEventField.onChange(e);
                    }}
                  />
                  {errors.guestLimitPerEvent && (
                    <p className="text-xs text-destructive">{errors.guestLimitPerEvent.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="ev-party-size">Max guests per signup (optional)</Label>
                <Input
                  id="ev-party-size"
                  type="text"
                  inputMode="numeric"
                  placeholder={ph("Leave empty to use the platform default")}
                  aria-invalid={!!errors.guestLimitPerSignup}
                  {...guestLimitPerSignupField}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                    guestLimitPerSignupField.onChange(e);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  How many guests one guestlist signup can include. Set to 1 to hide this option on
                  the guestlist form.
                </p>
                {errors.guestLimitPerSignup && (
                  <p className="text-xs text-destructive">{errors.guestLimitPerSignup.message}</p>
                )}
              </div>

              {/* Retail + Discounted price */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="ev-price">Retail price ($) *</Label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      $
                    </span>
                    <Input
                      id="ev-price"
                      className="pl-6"
                      type="text"
                      inputMode="decimal"
                      placeholder={ph("20.00")}
                      aria-invalid={!!errors.price}
                      {...priceField}
                      onChange={(e) => {
                        e.target.value = e.target.value
                          .replace(/[^\d.]/g, "")
                          .replace(/(\..*)\./g, "$1");
                        priceField.onChange(e);
                      }}
                    />
                  </div>
                  {errors.price ? (
                    <p className="text-xs text-destructive">{errors.price.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Max ${maxPriceDollars.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ev-disc-price">Discounted price ($) *</Label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      $
                    </span>
                    <Input
                      id="ev-disc-price"
                      className="pl-6"
                      type="text"
                      inputMode="decimal"
                      placeholder={ph("15.00")}
                      aria-invalid={!!errors.discountedPrice}
                      {...discountedPriceField}
                      onChange={(e) => {
                        e.target.value = e.target.value
                          .replace(/[^\d.]/g, "")
                          .replace(/(\..*)\./g, "$1");
                        discountedPriceField.onChange(e);
                      }}
                    />
                  </div>
                  {errors.discountedPrice && (
                    <p className="text-xs text-destructive">{errors.discountedPrice.message}</p>
                  )}
                </div>
              </div>

              {/* Conditions */}
              <div className="space-y-1">
                <Label htmlFor="ev-cond-input">Conditions (max 5)</Label>
                {!isPastEvent && (
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-1">
                      <Input
                        id="ev-cond-input"
                        value={conditionInput}
                        onChange={(e) => {
                          setConditionInput(e.target.value);
                          setConditionError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const trimmed = conditionInput.trim();
                            if (trimmed && trimmed.length < 3) {
                              setConditionError("Must be at least 3 characters.");
                            } else if (trimmed && conditions.length < 5) {
                              setConditions((prev) => [...prev, trimmed]);
                              setConditionInput("");
                              setConditionError(null);
                            }
                          }
                        }}
                        placeholder={ph("e.g. Must be 19+")}
                        maxLength={60}
                        disabled={conditions.length >= 5}
                      />
                      <p
                        className={`text-xs text-right ${
                          conditionInput.length > 60 ? "text-destructive" : "text-muted-foreground"
                        }`}
                      >
                        {conditionInput.length}/60
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const trimmed = conditionInput.trim();
                        if (trimmed && trimmed.length < 3) {
                          setConditionError("Must be at least 3 characters.");
                        } else if (trimmed && conditions.length < 5) {
                          setConditions((prev) => [...prev, trimmed]);
                          setConditionInput("");
                          setConditionError(null);
                        }
                      }}
                      disabled={!conditionInput.trim() || conditions.length >= 5}
                    >
                      Add
                    </Button>
                  </div>
                )}
                {conditionError && <p className="text-xs text-destructive">{conditionError}</p>}
                {conditions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {conditions.map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs"
                      >
                        {c}
                        {!isPastEvent && (
                          <button
                            type="button"
                            onClick={() => setConditions((prev) => prev.filter((_, j) => j !== i))}
                            className="hover:text-foreground"
                          >
                            <X className="size-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Venue */}
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={hasVenue}
                  disabled={isPastEvent}
                  onCheckedChange={(checked) =>
                    setValue("hasVenue", !!checked, { shouldDirty: true, shouldValidate: true })
                  }
                  aria-label="This event has its own venue"
                />
                <span className="text-sm">This event has its own venue</span>
              </label>
              {hasVenue && (
                <>
                  <TextInput
                    id="ev-venue-name"
                    label="Venue name"
                    placeholder={ph("e.g. VIP Lounge")}
                    maxLength={100}
                    error={errors.venueName?.message}
                    {...register("venueName")}
                  />
                  <Controller
                    control={control}
                    name="venueAddressLine1"
                    render={({ field }) => (
                      <AddressAutocomplete
                        label="Street address"
                        value={field.value}
                        onChange={field.onChange}
                        maxLength={200}
                        onSelect={(details: PlaceDetails) => {
                          field.onChange(details.address_line1 ?? "");
                          setValue("venueCity", details.city ?? "", {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setValue("venueProvince", details.province ?? "", {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setValue("venuePostalCode", details.postal_code ?? "", {
                            shouldDirty: true,
                          });
                        }}
                        error={errors.venueAddressLine1?.message}
                      />
                    )}
                  />
                  <TextInput
                    id="ev-venue-address2"
                    label="Suite / unit (optional)"
                    maxLength={200}
                    error={errors.venueAddressLine2?.message}
                    {...register("venueAddressLine2")}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <TextInput
                      id="ev-venue-city"
                      label="City"
                      maxLength={100}
                      error={errors.venueCity?.message}
                      {...register("venueCity")}
                    />
                    <TextInput
                      id="ev-venue-province"
                      label="Province"
                      maxLength={100}
                      error={errors.venueProvince?.message}
                      {...register("venueProvince")}
                    />
                  </div>
                  <TextInput
                    id="ev-venue-postal"
                    label="Postal code (optional)"
                    maxLength={20}
                    error={errors.venuePostalCode?.message}
                    {...register("venuePostalCode")}
                  />
                </>
              )}

              {/* Visibility */}
              <div className="space-y-1">
                <Controller
                  control={control}
                  name="visibility"
                  render={({ field }) => (
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={field.value === "unlisted"}
                        disabled={isPastEvent}
                        onCheckedChange={(checked) => {
                          const nextVisibility = checked ? "unlisted" : "public";
                          field.onChange(nextVisibility);
                          const currentCategoryId = watch("categoryId");
                          const stillVisible = categories.some(
                            (c) =>
                              c.id === currentCategoryId &&
                              categoryMatchesVisibility(c, nextVisibility)
                          );
                          if (!stillVisible) {
                            setValue("categoryId", "", { shouldDirty: true });
                          }
                        }}
                        aria-label="Unlisted event"
                      />
                      <span className="text-sm">Unlisted event</span>
                    </label>
                  )}
                />
                <p className="text-xs text-muted-foreground pl-7">
                  Unlisted events are hidden from all discovery feeds — reachable only via a link
                  the owner shares directly.
                </p>
              </div>
              {visibleCategories.length > 0 && (
                <Controller
                  control={control}
                  name="categoryId"
                  render={({ field }) => (
                    <Select
                      id="ev-category"
                      label="Category (optional)"
                      disabled={isPastEvent}
                      options={[
                        { value: NO_CATEGORY, label: "None" },
                        ...visibleCategories.map((c) => ({ value: c.id, label: c.name })),
                      ]}
                      value={field.value || NO_CATEGORY}
                      onValueChange={(v) => field.onChange(v === NO_CATEGORY ? "" : v)}
                    />
                  )}
                />
              )}
            </fieldset>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              {!isPastEvent && (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner className="size-4 border-background" />
                  ) : event && !isDuplicate ? (
                    "Save"
                  ) : (
                    "Create"
                  )}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
